import * as React from "react";
import {
  FieldValues,
  FormProvider,
  Path,
  useController,
  useForm,
  useFormContext,
} from "react-hook-form";
import { BorrowerChild } from "@tlc/ls2pac/dto/BorrowerChild";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useLocationConfiguration } from "@tlc/ls2pac/components/LocationConfiguration";
import { AbstractBaseResponse } from "@tlc/ls2pac/components/ForgotPINDialog";
import {
  Alert,
  Checkbox,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from "@mui/material";
import axios from "axios";
import { useEffect } from "react";
import { FormDialog } from "@tlc/ui/dialog/FormDialog";

interface SetGuardianProhibitedListChildrenResponse
  extends AbstractBaseResponse {}
async function setProhibitedListChildren(listId: number, childIds: number[]) {
  const { data } = await axios.post<SetGuardianProhibitedListChildrenResponse>(
    `/list/prohibited-list/${listId}/children`,
    childIds,
  );
  return data.status;
}

interface ChildCheckboxGroupProps<T extends FieldValues> {
  students: BorrowerChild[];
  name: Path<T>;
  label: string;
}
export const ChildCheckboxGroup = <T extends FieldValues>({
  students,
  name,
  label,
}: ChildCheckboxGroupProps<T>) => {
  const { translate } = useLanguage();
  const { control } = useFormContext<T>();
  const {
    field: { value, onChange },
  } = useController({ name, control });
  const { locationConfiguration } = useLocationConfiguration();

  if (!students) {
    return null;
  }

  const configLocationIds = (locationConfiguration?.filterBranches || []).map(
    (branch) => parseInt(branch.identifier),
  );

  const toggleId = (id: number) => {
    const selectedIds: number[] = value || [];
    const newIds = selectedIds.includes(id)
      ? selectedIds.filter((i) => i !== id)
      : [...selectedIds, id];
    onChange(newIds);
  };

  const getChildCheckbox = (child: BorrowerChild) => {
    return (
      <FormControlLabel
        key={child.id}
        control={
          <Checkbox
            checked={(value || []).includes(child.id)}
            onChange={() => toggleId(child.id)}
          />
        }
        label={`${child.firstName} ${child.lastName}`}
      />
    );
  };

  if (configLocationIds.length === 0) {
    return (
      <FormControl component="fieldset" variant="standard">
        <FormLabel component="legend">{label}</FormLabel>
        <FormGroup>
          {students.map((child) => getChildCheckbox(child))}
        </FormGroup>
      </FormControl>
    );
  }

  const schoolStudents = students.filter(
    (child) => configLocationIds.indexOf(child.issuingLocation) !== -1,
  );
  const notSchoolStudents = students.filter(
    (child) => configLocationIds.indexOf(child.issuingLocation) === -1,
  );

  return (
    <FormControl component="fieldset" variant="standard">
      <FormLabel component="legend">{label}</FormLabel>
      <FormGroup>
        {schoolStudents.length > 0 && (
          <>
            <DialogContentText>
              {locationConfiguration.libraryName
                ? translate("studentsAtLibraryName", {
                    LIBRARY_NAME: locationConfiguration.libraryName,
                  })
                : translate("studentsAtThisSchool")}
            </DialogContentText>
            {schoolStudents.map((child) => getChildCheckbox(child))}
          </>
        )}
        {notSchoolStudents.length > 0 && (
          <>
            <DialogContentText>
              {locationConfiguration.libraryName
                ? translate("studentsNotAtLibraryName", {
                    LIBRARY_NAME: locationConfiguration.libraryName,
                  })
                : translate("studentsNotAtThisSchool")}
            </DialogContentText>
            <Alert severity="info">{translate("studentsNotAtHint")}</Alert>

            {notSchoolStudents.map((child) => getChildCheckbox(child))}
          </>
        )}
      </FormGroup>
    </FormControl>
  );
};

export interface ChildSelectionDialogProps {
  open: boolean; // externally controlled open state
  onClose: () => void;
  onSave: (update: BorrowerChild["id"][]) => void;
  students: BorrowerChild[];
  listId: number;
  childIds: BorrowerChild["id"][];
}

interface ChildSelectionFormInput {
  checkedChildIds: number[];
}

export const ChildSelectionDialog: React.FC<ChildSelectionDialogProps> = ({
  open,
  onClose,
  onSave,
  students,
  childIds,
  listId,
}) => {
  const formMethods = useForm<ChildSelectionFormInput>({
    mode: "onBlur",
    defaultValues: {
      checkedChildIds: childIds,
    },
  });
  const { handleSubmit, control, reset } = formMethods;
  const { translate } = useLanguage();

  const onSubmit = async ({ checkedChildIds }: ChildSelectionFormInput) => {
    const { success, message } = await setProhibitedListChildren(
      listId,
      checkedChildIds,
    );
    if (success) {
      onSave(checkedChildIds);
    } else {
      // TODO we have no pre-existing error handling here
      console.error(message);
    }
  };

  useEffect(() => {
    if (open) {
      reset({ checkedChildIds: childIds });
    }
  }, [open, reset, childIds]);

  const formId = "child-selection-dialog";

  return (
    <FormDialog
      formId={formId}
      open={open}
      onClose={onClose}
      confirmButtonText={translate("save")}
      headline={translate("associatedStudents")}
    >
      <form
        id={formId}
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormProvider {...formMethods}>
          <ChildCheckboxGroup<ChildSelectionFormInput>
            label={translate("associatedStudentsDirections")}
            students={students}
            name={"checkedChildIds"}
          />
        </FormProvider>
      </form>
    </FormDialog>
  );
};
